import { GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Box } from "@mui/material";
import CustomDataGridV2 from "../../../../../../../../components/CustomDataGridV2";
import StatusChip from "../../../../../../../../components/StatusChip";
import ActionMenu from "../../../../../../../../components/app/ActionMenu";
import DeleteModal from "../../../../../../../../components/app/DeleteModal";
import Input from "../../../../../../../../components/Input";
import useDisclosure from "../../../../../../../../hooks/use-disclosure";
import { useReduceDisclosure } from "../../../../../../../../hooks/use-reduce-disclosure";
import { useSnackbar } from "../../../../../../../../hooks/useSnackbar";
import { useArchivePrescription } from "../../../../../../../../queries/patient-chart/order/prescription/use-prescription-archive";
import { usePrescriptionStatusChange } from "../../../../../../../../queries/patient-chart/order/prescription/use-prescription-status-change";
import { UpdatePrescriptionStatusStatusEnum } from "../../../../../../../../services";
import { ALIAS, PRIMITIVE } from "../../../../../../../../tokens/design-tokens";
import { formatDate } from "../../../../../../../../utils/date-time-utility";
import {
  PAGE_SIZE_OPTIONS,
  Page,
  applyDefaultColumnSettings,
  getValueFromObj,
  replaceUnderScoreAddSpace,
  showToastOnApiCall,
} from "../../../../../../../../utils/utils";
import UpsertPrescriptionForm from "../../../../../scheduling/appointment/encounter/prescription/UpsertPrescriptionForm";
import PrintPrescription from "./PrintPrescription";
import ViewCancelReason from "./ViewCancelReason";
import { getTransPayload } from "./OrderPrescriptionTableNew";

interface PrescriptionTableProps {
  data?: { content: any[]; page: Page };
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  onViewPrescription?: (row: any) => void;
}

const STATUS_COLOR_MAPPER: Record<string, { color: string; bgColor: string }> =
  {
    DRAFT: {
      color: ALIAS.semantics.warning.pressed,
      bgColor: ALIAS.semantics.warning.bg,
    },
    ORDERED: {
      color: PRIMITIVE.color.brandBlue[800],
      bgColor: PRIMITIVE.color.brandBlue[50],
    },
    DELIVERED: {
      color: ALIAS.semantics.success.pressed,
      bgColor: ALIAS.semantics.success.bg,
    },
    CANCELED: {
      color: ALIAS.semantics.error.pressed,
      bgColor: ALIAS.semantics.error.bg,
    },
  };

const getActionMenus = (status: string) => {
  const menus: Record<string, { label: string; value: string }[]> = {
    DRAFT: [
      { label: "Edit", value: "edit" },
      { label: "Ordered", value: "ordered" },
      { label: "View & Print", value: "view" },
    ],
    ORDERED: [
      { label: "View & Print", value: "view" },
      { label: "Delivered", value: "delivered" },
      { label: "Cancel", value: "cancel" },
    ],
    DELIVERED: [{ label: "View & Print", value: "view" }],
    CANCELED: [
      { label: "View & Print", value: "view" },
      { label: "Cancellation Reason", value: "view_reason" },
    ],
  };
  return menus[status] ?? [];
};

const PrescriptionTable = ({
  data,
  paginationModel,
  onPaginationModelChange,
  onViewPrescription,
}: PrescriptionTableProps) => {
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();
  const [prefillData, setPrefillData] = useState<any>(undefined);
  const [selectedPrescriptionId, setSelectedPrescriptionId] =
    useState<string>();
  const [isViewOnly, setIsViewOnly] = useState(false);

  const [prescriptionDisc, deleteDisc, viewCancelReasonDisc, cancelReasonDisc] =
    useReduceDisclosure(4);
  const printViewDisclosure = useDisclosure(false);

  const { mutateAsync: archivePrescription } = useArchivePrescription();
  const { mutateAsync: prescriptionStatusChange } =
    usePrescriptionStatusChange();

  const handleStatusChange = async (
    prescriptionId: string,
    status: UpdatePrescriptionStatusStatusEnum,
    reason?: string
  ) => {
    showToastOnApiCall({
      showSnackbar,
      apiCall: prescriptionStatusChange({
        prescriptionFormId: prescriptionId,
        status,
        reason,
      }),
      onSave: () => {
        queryClient.invalidateQueries({
          queryKey: ["patient-prescription-list"],
        });
      },
    });
  };

  const handlePrint = (prescriptionId: string) => {
    setSelectedPrescriptionId(prescriptionId);
    printViewDisclosure.open(prescriptionId);
  };

  const onActionClick = (v: string, row: any) => {
    setIsViewOnly(false);
    const transformedPayload = getTransPayload(row);

    if (v === "edit") {
      setPrefillData(transformedPayload);
      prescriptionDisc.open(row.uuid);
    } else if (v === "view") {
      if (onViewPrescription) {
        onViewPrescription(row);
      } else {
        handlePrint(row?.uuid);
      }
    } else if (v === "delete") {
      deleteDisc?.open(row.uuid);
    } else if (v === "print") {
      handlePrint(row?.uuid);
    } else if (v === "ordered") {
      handleStatusChange(row?.uuid, "ORDERED");
    } else if (v === "delivered") {
      handleStatusChange(row?.uuid, "DELIVERED");
    } else if (v === "cancel") {
      cancelReasonDisc.open(row?.uuid);
    } else if (v === "view_reason") {
      viewCancelReasonDisc.open(row?.reason);
    }
  };

  const columns: GridColDef[] = applyDefaultColumnSettings([
    {
      field: "prescriptionDate",
      headerName: "Prescription Date",
      flex: 0.8,
      minWidth: 130,
      renderCell: ({ row }) => formatDate(row?.prescriptionDate) ?? "-",
    },
    {
      field: "provider",
      headerName: "Prescriber",
      flex: 1,
      minWidth: 140,
      renderCell: ({ row }) =>
        row?.provider ? (Object.values(row.provider)?.[0] as string) : "-",
    },
    {
      field: "preferences",
      headerName: "Pharmacy",
      flex: 1.2,
      minWidth: 160,
      renderCell: ({ row }) => getValueFromObj(row?.preferences) || "-",
    },
    {
      field: "prescriptionStatus",
      headerName: "Status",
      flex: 0.5,
      minWidth: 100,
      renderCell: ({ row }) => {
        if (!row?.prescriptionStatus) return "-";
        const colors = STATUS_COLOR_MAPPER[row.prescriptionStatus];
        return (
          <StatusChip
            label={replaceUnderScoreAddSpace(
              row.prescriptionStatus.toLowerCase()
            )}
            color={colors?.color}
            bgColor={colors?.bgColor}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.3,
      minWidth: 50,
      renderCell: ({ row }) => (
        <ActionMenu
          options={getActionMenus(row?.prescriptionStatus)}
          onActionClick={(v) => onActionClick(v, row)}
        />
      ),
    },
  ]);

  const cancelReasonFormik = useFormik({
    initialValues: { reason: "" },
    validationSchema: Yup.object({
      reason: Yup.string().trim().required("Reason is required"),
    }),
    onSubmit: () => {},
  });

  const archiveHandler = async (value: boolean, id: any) => {
    return archivePrescription({
      prescriptionFormId: id?.toString() ?? "",
      status: value,
    }).then((res) => {
      queryClient.invalidateQueries({
        queryKey: ["patient-prescription-list"],
      });
      return res;
    });
  };

  return (
    <>
      <DeleteModal
        disableOnSaveSnackbar
        disabledConfirmation={
          !cancelReasonFormik.isValid ||
          !cancelReasonFormik.values.reason?.trim()
        }
        w="450px"
        open={cancelReasonDisc.opened}
        onClose={cancelReasonDisc.close}
        id={cancelReasonDisc.data}
        onConfirmation={async (id) => {
          if (!cancelReasonFormik.values.reason) {
            cancelReasonFormik.validateForm();
            return;
          }
          return handleStatusChange(
            id as any,
            "CANCELED",
            cancelReasonFormik.values.reason
          );
        }}
        content="You want to cancel this prescription?"
        input={
          <Box width="100%">
            <Input
              required
              error={cancelReasonFormik.errors.reason}
              label="Reason for cancellation"
              textFieldProps={{
                name: "reason",
                onBlur: cancelReasonFormik.handleBlur,
                multiline: true,
                rows: 2,
                placeholder: "Enter a Reason...",
                value: cancelReasonFormik.values.reason,
                onChange: (e) =>
                  cancelReasonFormik.setFieldValue("reason", e.target.value),
              }}
            />
          </Box>
        }
      />

      <DeleteModal
        w="380px"
        open={!!deleteDisc?.opened}
        onClose={deleteDisc?.close!}
        id={deleteDisc?.data}
        onConfirmation={(id) => archiveHandler(true, id)}
        content="You want to delete this Prescription Order"
      />

      {printViewDisclosure.data && (
        <PrintPrescription
          prescriptionFormId={selectedPrescriptionId}
          onClose={() => setSelectedPrescriptionId(undefined)}
        />
      )}

      <UpsertPrescriptionForm
        prefillData={prefillData}
        setPrefillData={setPrefillData}
        disclosureHost={prescriptionDisc}
        readOnly={isViewOnly}
        onOrder={(id: string) => handlePrint(id)}
      />

      <ViewCancelReason disclosureHost={viewCancelReasonDisc} />

      <CustomDataGridV2
        rows={data?.content ?? []}
        columns={columns}
        getRowId={(row) => row?.uuid}
        rowCount={data?.page?.totalElements ?? 0}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        paginationMode="server"
        pageSizeOptions={PAGE_SIZE_OPTIONS}
      />
    </>
  );
};

export default PrescriptionTable;
